import React from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Form, Select, Input, Alert } from 'antd';
import SelectOrAdd from 'components/SelectOrAdd';

import useDataSource from 'hooks/useDataSource';
import useFormatMessage from 'hooks/useFormatMessage';
import LicenseTag from 'components/LicenseTag';
import messages from './messages';

const REPORTS_QUERY = gql`
  query($customerIds: [ID!]!){
    reports(customerIds: $customerIds) {
      id
      title
      content {
        id
        filter {
          id
          dataSource {
            id
          }
        }
      }
    }
  }
`;

const CREATE_REPORT = gql`
  mutation ($title: String!, $customerId: ID!) {
    createReport(title: $title, customerId: $customerId) {
      id
      title
    }
  }
`;

const { Option } = Select;
const { TextArea } = Input;

const AddChartToReportForm = ({ customerId, form, onSubmit }) => {
  const $t = useFormatMessage();
  const { loading, data: { reports = [] } = {} } = useQuery(REPORTS_QUERY, { variables: { customerIds: [customerId] } });
  const [createReport, { loading: creating }] = useMutation(CREATE_REPORT);
  const { dataSourceId, getDataSourceLicenseSetting } = useDataSource();
  const reportLimit = getDataSourceLicenseSetting('reportLimit');

  const handleCreateReport = async (title) => {
    try {
      await createReport({
        variables: { title, customerId },
        refetchQueries: () => [{ query: REPORTS_QUERY, variables: { customerIds: [customerId] } }],
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onFinish = (values) => {
    const { title } = reports.find(r => r.id === values.report);
    const { report: reportId, chartComment } = values;
    onSubmit(title, reportId, chartComment);
  };

  const reportsWithContentFromWebsite = reports.filter(r => r.content.some(c => c.filter.dataSource === dataSourceId));

  const reachedReportLimit = reportLimit > 0 && reportsWithContentFromWebsite.length >= reportLimit;

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" colon={false}>
      {reachedReportLimit && (
        <Form.Item>
          <Alert
            message={(
              <>
                <LicenseTag setting="reportLimit" showTooltip />
                {$t(messages.reachedReportLimit)}
              </>
            )}
            type="warning"
          />
        </Form.Item>
      )}
      <Form.Item
        name="report"
        label={$t(messages.reportLabel)}
        rules={[{ required: true, message: $t(messages.reportRequiredError) }]}
      >
        <SelectOrAdd
          showSearch
          optionFilterProp="children"
          onAdd={!reachedReportLimit && handleCreateReport}
          addButtonLabel={$t(messages.createReport)}
          inputTooltip={$t(messages.reportSelectAddTooltip)}
          placeholder={$t(messages.reportSelectPlaceholder)}
          loading={loading || creating}
          disabled={loading}
        >
          {reports.map(report => <Option key={report.id} value={report.id} disabled={reachedReportLimit && !reportsWithContentFromWebsite.find(r => r.id === report.id)}>{report.title}</Option>)}
        </SelectOrAdd>
      </Form.Item>
      <Form.Item name="chartComment" label={$t(messages.commentLabel)}>
        <TextArea rows={4} placeholder={$t(messages.commentPlaceholder)} />
      </Form.Item>
    </Form>
  );
};

export default AddChartToReportForm;
